import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './AudioControlDrawer.css';

const AudioControlDrawer = ({ audioElement, isOpen, onToggle, getSharedAudioNodes }) => {
  // EQ state
  const [eqValues, setEqValues] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [activePreset, setActivePreset] = useState('Normal');
  
  // BPM and Pitch state
  const [bpm, setBpm] = useState(120);
  const [pitchShift, setPitchShift] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  
  // Audio processing refs - will use shared nodes from MusicPlayer
  const sharedNodesRef = useRef(null);
  const pitchShifterRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  
  // Pitch shifting using Web Audio API
  const initializePitchShifter = () => {
    const sharedNodes = sharedNodesRef.current;
    if (!sharedNodes?.audioContext) return;
    
    try {
      // Create a script processor for pitch shifting
      const bufferSize = 4096;
      const processor = sharedNodes.audioContext.createScriptProcessor(bufferSize, 1, 1);
      
      // Simple pitch shifting using playback rate and resampling
      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer.getChannelData(0);
        const outputBuffer = event.outputBuffer.getChannelData(0);
        
        // Apply pitch shift by changing the playback rate
        for (let i = 0; i < outputBuffer.length; i++) {
          outputBuffer[i] = inputBuffer[i];
        }
      };
      
      scriptProcessorRef.current = processor;
      return processor;
    } catch (error) {
      console.error('Error creating pitch shifter:', error);
      return null;
    }
  };

  const frequencies = [32, 64, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
  const frequencyLabels = ['32Hz', '64Hz', '125Hz', '250Hz', '500Hz', '1kHz', '2kHz', '4kHz', '8kHz', '16kHz'];
  const instrumentLabels = [
    'Sub Bass\nRumble',
    'Bass Drum\nKick',
    'Bass\nLow End',
    'Low Mids\nWarmth',
    'Mids\nVocals',
    'Upper Mids\nClarity',
    'Presence\nVocals',
    'Brilliance\nCymbals',
    'Air\nSparkle',
    'Ultra High\nShimmer'
  ];

  const presets = {
    'Normal': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    'Rock': [4, 3, -1, -2, 1, 2, 3, 4, 3, 2],
    'Pop': [2, 1, 0, 1, 2, 2, 1, 0, 1, 2],
    'Jazz': [3, 2, 0, 1, 2, 1, 2, 3, 2, 1],
    'Classical': [3, 2, 1, 0, -1, 0, 1, 2, 3, 4],
    'Electronic': [5, 3, 1, 0, 2, 3, 2, 4, 5, 3]
  };

  const initializeSharedNodes = () => {
    if (!getSharedAudioNodes) {
      console.warn('[AudioControlDrawer] getSharedAudioNodes not provided');
      return;
    }

    try {
      console.log('[AudioControlDrawer] Getting shared audio nodes from MusicPlayer...');
      const sharedNodes = getSharedAudioNodes();
      
      if (!sharedNodes.audioContext || !sharedNodes.eqFilters) {
        console.warn('[AudioControlDrawer] Shared audio nodes not ready yet');
        return;
      }

      sharedNodesRef.current = sharedNodes;
      
      // Apply current EQ values to the shared filters
      sharedNodes.eqFilters.forEach((filter, index) => {
        if (filter && index < eqValues.length) {
          filter.gain.value = eqValues[index];
        }
      });

      console.log('[AudioControlDrawer] Successfully connected to shared audio nodes');
    } catch (error) {
      console.error('[AudioControlDrawer] Error connecting to shared audio nodes:', error);
    }
  };

  useEffect(() => {
    if (isOpen && getSharedAudioNodes) {
      // Try to initialize immediately
      initializeSharedNodes();
      
      // If not ready, try again after a short delay
      const retryTimeout = setTimeout(() => {
        if (!sharedNodesRef.current) {
          initializeSharedNodes();
        }
      }, 500);

      return () => {
        clearTimeout(retryTimeout);
      };
    }

    return () => {
      // Clean up pitch shifter if it exists
      if (scriptProcessorRef.current) {
        try {
          scriptProcessorRef.current.disconnect();
        } catch (error) {
          console.warn('Error disconnecting script processor:', error);
        }
        scriptProcessorRef.current = null;
      }
    };
  }, [isOpen, getSharedAudioNodes]);

  const handleEQChange = (index, value) => {
    const newValues = [...eqValues];
    newValues[index] = parseFloat(value);
    setEqValues(newValues);

    // Apply to shared EQ filters
    const sharedNodes = sharedNodesRef.current;
    if (sharedNodes?.eqFilters?.[index]) {
      sharedNodes.eqFilters[index].gain.value = parseFloat(value);
      console.log(`[AudioControlDrawer] EQ ${frequencies[index]}Hz: ${value}dB`);
    } else {
      console.warn(`[AudioControlDrawer] Shared EQ filter ${index} not available`);
    }
  };

  const handlePresetChange = (presetName) => {
    setActivePreset(presetName);
    const presetValues = presets[presetName];
    setEqValues(presetValues);

    // Apply preset to shared EQ filters
    const sharedNodes = sharedNodesRef.current;
    if (sharedNodes?.eqFilters) {
      presetValues.forEach((value, index) => {
        if (sharedNodes.eqFilters[index]) {
          sharedNodes.eqFilters[index].gain.value = value;
        }
      });
      console.log(`[AudioControlDrawer] Applied preset: ${presetName}`);
    } else {
      console.warn(`[AudioControlDrawer] Shared EQ filters not available for preset: ${presetName}`);
    }
  };

  // BPM Control Functions
  const adjustBPM = (increment) => {
    const newBPM = Math.max(50, Math.min(200, bpm + increment));
    setBpm(newBPM);
    
    // Calculate playback rate based on BPM change
    const originalBPM = 120; // Assuming original track is 120 BPM
    const newRate = newBPM / originalBPM;
    setPlaybackRate(newRate);
    
    if (audioElement) {
      audioElement.playbackRate = newRate;
    }
    
    console.log(`[AudioControlDrawer] BPM: ${newBPM}, Playback Rate: ${newRate.toFixed(2)}`);
  };

  const handleBPMSliderChange = (e) => {
    const newBPM = parseInt(e.target.value);
    setBpm(newBPM);
    
    const originalBPM = 120;
    const newRate = newBPM / originalBPM;
    setPlaybackRate(newRate);
    
    if (audioElement) {
      audioElement.playbackRate = newRate;
    }
  };

  const handleBPMInputChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 50 && value <= 200) {
      setBpm(value);
      
      const originalBPM = 120;
      const newRate = value / originalBPM;
      setPlaybackRate(newRate);
      
      if (audioElement) {
        audioElement.playbackRate = newRate;
      }
    }
  };

  // Pitch Control Functions
  const handlePitchChange = (e) => {
    const newPitch = parseInt(e.target.value);
    setPitchShift(newPitch);
    
    // Apply pitch shift using preservesPitch and playbackRate
    if (audioElement) {
      try {
        // Modern browsers support preservesPitch
        audioElement.preservesPitch = false;
        
        // Calculate pitch shift factor (semitone = 2^(1/12))
        const pitchFactor = Math.pow(2, newPitch / 12);
        
        // Apply pitch shift by modifying playback rate
        // Note: This also affects tempo, for true pitch shifting without tempo change,
        // we'd need more complex audio processing
        const currentRate = playbackRate;
        audioElement.playbackRate = currentRate * pitchFactor;
        
        console.log(`[AudioControlDrawer] Pitch: ${newPitch} semitones, Factor: ${pitchFactor.toFixed(3)}`);
      } catch (error) {
        console.warn('[AudioControlDrawer] Pitch shifting not fully supported:', error);
      }
    }
  };

  const resetPitch = () => {
    setPitchShift(0);
    if (audioElement) {
      audioElement.preservesPitch = true;
      audioElement.playbackRate = playbackRate; // Reset to current BPM rate only
    }
  };

  // Auto-detect BPM (basic implementation)
  const detectBPM = () => {
    const sharedNodes = sharedNodesRef.current;
    if (!sharedNodes?.analyser || !audioElement) return;
    
    const dataArray = new Uint8Array(sharedNodes.analyser.frequencyBinCount);
    sharedNodes.analyser.getByteFrequencyData(dataArray);
    
    // Simple BPM detection based on low frequency energy
    const lowFreqEnergy = dataArray.slice(0, 10).reduce((sum, val) => sum + val, 0);
    
    // This is a very basic implementation
    // Real BPM detection would require more sophisticated algorithms
    const estimatedBPM = Math.round(60 + (lowFreqEnergy / 10));
    const clampedBPM = Math.max(80, Math.min(180, estimatedBPM));
    
    setBpm(clampedBPM);
    console.log(`[AudioControlDrawer] Auto-detected BPM: ${clampedBPM}`);
  };

  const formatPitchDisplay = (semitones) => {
    if (semitones === 0) return '0';
    return semitones > 0 ? `+${semitones}` : `${semitones}`;
  };

  if (!isOpen) return null;

  return (
    <div className="audio-control-drawer">
      <div className="drawer-backdrop" onClick={onToggle}></div>
      <div className="drawer-content">
        <div className="drawer-header">
          <h5>Audio Controls</h5>
          <button className="drawer-close-btn" onClick={onToggle}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="drawer-body">
          {/* Equalizer Section */}
          <div className="control-section">
            <div className="section-header">
              <h6>Equalizer</h6>
            </div>
            
            <div className="preset-buttons">
              {Object.keys(presets).map(preset => (
                <button
                  key={preset}
                  className={`preset-btn ${activePreset === preset ? 'active' : ''}`}
                  onClick={() => handlePresetChange(preset)}
                >
                  {preset}
                </button>
              ))}
            </div>
            
            <div className="eq-controls">
              {frequencies.map((freq, index) => (
                <div key={freq} className="eq-band">
                  <input
                    type="range"
                    className="eq-slider"
                    min="-12"
                    max="12"
                    step="0.5"
                    value={eqValues[index]}
                    onChange={(e) => handleEQChange(index, e.target.value)}
                    orient="vertical"
                  />
                  <div className="eq-label">
                    <div className="eq-freq">{frequencyLabels[index]}</div>
                    <div className="eq-instrument">{instrumentLabels[index]}</div>
                    <div className="eq-value">{eqValues[index] > 0 ? '+' : ''}{eqValues[index]}dB</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* BPM Control Section */}
          <div className="control-section">
            <div className="section-header">
              <h6>BPM Control</h6>
            </div>
            
            <div className="bpm-control">
              <div className="control-row">
                <span className="control-label">Beats Per Minute</span>
                <div className="bpm-input-group">
                  <button className="bpm-btn" onClick={() => adjustBPM(-1)}>−</button>
                  <input
                    type="number"
                    className="bpm-input"
                    value={bpm}
                    onChange={handleBPMInputChange}
                    min="50"
                    max="200"
                  />
                  <button className="bpm-btn" onClick={() => adjustBPM(1)}>+</button>
                </div>
              </div>
              
              <input
                type="range"
                className="bpm-slider"
                min="50"
                max="200"
                value={bpm}
                onChange={handleBPMSliderChange}
              />
              
              <div className="slider-labels">
                <span>50 BPM</span>
                <span>200 BPM</span>
              </div>
              
              <div className="control-row">
                <span className="control-label">Playback Rate: {playbackRate.toFixed(2)}x</span>
                <button className="pitch-btn" onClick={detectBPM}>Auto-Detect BPM</button>
              </div>
            </div>
          </div>

          {/* Pitch Control Section */}
          <div className="control-section">
            <div className="section-header">
              <h6>Pitch Control</h6>
            </div>
            
            <div className="pitch-control">
              <div className="control-row">
                <span className="control-label">Pitch Shift</span>
                <div className="pitch-display">{formatPitchDisplay(pitchShift)} semitones</div>
              </div>
              
              <input
                type="range"
                className="pitch-slider"
                min="-12"
                max="12"
                value={pitchShift}
                onChange={handlePitchChange}
              />
              
              <div className="slider-labels">
                <span>-12</span>
                <span>0</span>
                <span>+12</span>
              </div>
              
              <div className="pitch-buttons">
                <button className="pitch-btn" onClick={resetPitch}>Reset Pitch</button>
              </div>
              
              <div className="coming-soon">
                <small className="text-muted">
                  Note: Pitch shifting affects playback speed.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioControlDrawer; 